import React, { useMemo } from 'react';
import { Check } from 'lucide-react';
import { BaseTool, IToolManifest, IToolComponentProps, IToolContext } from '../../core/tools/interfaces/ITool';
import type { ICommand } from '../../core/commands/ICommand';
import { ChangeNodeStyleCommand } from '../../core/commands/ChangeNodeStyleCommand';
import { cn } from '../../lib/utils';
import { STICKY_NOTE_COLORS } from './StickyNoteTool';
import { useTranslation } from '../../hooks/useTranslation';

type StickyNoteStyleParams = {
  fillColor?: string;
  strokeColor?: string;
  textColor?: string;
};

const StickyNotePropertiesPanel: React.FC<IToolComponentProps> = ({ context, onExecute }) => {
  const { t } = useTranslation('diagram');
  const selectionVersion = context.selectionSummary?.version;
  const notes = useMemo(() => {
    return context
      .getSelectedShapes()
      .map(shape => (shape as any)?.data)
      .filter((node: any) => node?.type === 'sticky-note');
  }, [selectionVersion]);

  if (notes.length === 0) {
    return (
      <div className="px-4 py-3 text-xs text-muted-foreground">
        Select a sticky note to edit its properties.
      </div>
    );
  }

  const selectionStyles = context.selectionStyles;
  const primary = notes[0] as any;

  const computedFill = selectionStyles?.fillColor || primary?.fillColor || primary?.attrs?.fillColor;
  const computedStroke = selectionStyles?.strokeColor || primary?.strokeColor || primary?.attrs?.strokeColor;
  const computedText = selectionStyles?.textColor ?? primary?.attrs?.textColor ?? '#0F172A';

  const hasMixedColor = !!(selectionStyles?.isMixed?.fillColor || selectionStyles?.isMixed?.strokeColor);
  const hasMixedText = !!selectionStyles?.isMixed?.textColor;

  const currentFill = (computedFill || '').toLowerCase();
  const currentStroke = (computedStroke || '').toLowerCase();
  const currentTextColor = (computedText || '').toLowerCase();

  const activeColorId = hasMixedColor
    ? undefined
    : STICKY_NOTE_COLORS.find(
        colour =>
          colour.color.toLowerCase() === currentFill &&
          colour.border.toLowerCase() === currentStroke
      )?.id;

  const isLightText = hasMixedText
    ? null
    : currentTextColor === '#ffffff' || currentTextColor === '#fff'
      ? true
      : currentTextColor
        ? false
        : null;

  const isDarkActive = isLightText === false;
  const isLightActive = isLightText === true;

  return (
    <div className="px-4 py-3 space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-foreground">{t('stickyNote.title', 'Sticky Note')}</h3>
        <p className="text-xs text-muted-foreground">
          {t('stickyNote.description', { count: notes.length, defaultValue: 'Update color and text contrast for the selected note{{s}}.', s: notes.length > 1 ? 's' : '' })}
        </p>
      </div>

      <div className="space-y-2">
        <div className="text-xs font-medium text-muted-foreground">{t('stickyNote.color', 'Color')}</div>
        <div className="grid grid-cols-4 gap-2">
          {STICKY_NOTE_COLORS.map(colour => {
            const isActive = colour.id === activeColorId;
            return (
              <button
                key={colour.id}
                type="button"
                data-testid={`tid.tools.stickyNote.swatch.${colour.id}`}
                onClick={() =>
                  onExecute({
                    fillColor: colour.color,
                    strokeColor: colour.border,
                  })
                }
                className={cn(
                  'h-10 rounded-lg border transition-transform focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1',
                  isActive ? 'scale-105 shadow-sm ring-2 ring-primary/60 ring-offset-2' : 'hover:scale-105'
                )}
                style={{
                  backgroundColor: colour.color,
                  borderColor: isActive ? '#3478DD' : colour.border,
                }}
                aria-label={colour.label}
              >
                {isActive && (
                  <div className="flex h-full w-full items-center justify-center">
                    <Check className="h-4 w-4 text-[#1F2937]" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        <div className="text-xs font-medium text-muted-foreground">{t('stickyNote.textContrast', 'Text contrast')}</div>
        <div className="flex gap-2">
          <button
            type="button"
            data-testid="tid.tools.stickyNote.darkText"
            onClick={() => onExecute({ textColor: '#0F172A' })}
            className={cn(
              'flex-1 rounded-md border px-3 py-2 text-xs font-medium transition-colors',
              isDarkActive ? 'border-primary bg-primary/10 text-primary' : 'border-muted hover:bg-muted/40'
            )}
          >
            {t('stickyNote.darkText', 'Dark text')}
          </button>
          <button
            type="button"
            data-testid="tid.tools.stickyNote.lightText"
            onClick={() => onExecute({ textColor: '#FFFFFF' })}
            className={cn(
              'flex-1 rounded-md border px-3 py-2 text-xs font-medium transition-colors',
              isLightActive ? 'border-primary bg-primary/10 text-primary' : 'border-muted hover:bg-muted/40'
            )}
          >
            {t('stickyNote.lightText', 'Light text')}
          </button>
        </div>
      </div>

      {notes.length > 1 && (
        <div className="rounded-md border border-dashed border-amber-300 bg-amber-50 px-3 py-2 text-[11px] font-medium text-amber-900">
          {t('stickyNote.applyingToMany', { count: notes.length, defaultValue: 'Applying changes will update all {{count}} selected sticky notes.' })}
        </div>
      )}
    </div>
  );
};

export class StickyNotePropertiesTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'sticky-note-properties',
    name: 'Sticky Note',
    icon: 'sticky-note',
    category: 'style',
    description: 'Adjust sticky note appearance',
    version: '1.0.0',
    requiresSelection: true,
    minSelection: 1,
    ui: {
      toolbar: false,
      contextMenu: false,
      propertyPanel: true,
      sidebar: false,
      floatingPanel: false,
      statusBar: false,
    },
    applicability: {
      contextTypes: ['sticky-note'],
    },
    capabilities: {
      modifiesShapes: true,
      createsShapes: false,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: true,
      supportsBatchOperations: true,
    },
  };

  private getSelectedStickyNoteIds(): string[] {
    if (!this.context) return [];
    return this.context
      .getSelectedShapes()
      .map(shape => (shape as any)?.data)
      .filter((node: any) => node?.type === 'sticky-note')
      .map((node: any) => node.id);
  }

  canExecute(context: IToolContext): boolean {
    if (!super.canExecute(context)) return false;
    const shapes = context.getSelectedShapes();
    return shapes.some(shape => (shape as any)?.data?.type === 'sticky-note');
  }

  execute(params?: StickyNoteStyleParams): ICommand | ICommand[] | null {
    if (!this.context) return null;
    const stickyNoteIds = this.getSelectedStickyNoteIds();
    if (stickyNoteIds.length === 0) {
      return null;
    }

    const commands: ICommand[] = [];
    if (params?.fillColor) {
      commands.push(new ChangeNodeStyleCommand(stickyNoteIds, 'fillColor', params.fillColor));
    }
    if (params?.strokeColor) {
      commands.push(new ChangeNodeStyleCommand(stickyNoteIds, 'strokeColor', params.strokeColor));
    }
    if (params?.textColor) {
      commands.push(new ChangeNodeStyleCommand(stickyNoteIds, 'textColor', params.textColor));
    }

    return commands.length > 0 ? commands : null;
  }

  getPropertyPanelComponent() {
    return StickyNotePropertiesPanel;
  }
}
